import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Container,
  Box,
  Flex,
  Stack,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Text,
  Spacer,
  Td,
  Input,
  ModalFooter,
  IconButton,
  HStack,
  NumberInput,
  NumberInputField,
  Checkbox,
  Center,
  Collapse,
  Tag,
  TagLabel,
  TagCloseButton,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Select,
} from "@chakra-ui/react";
import { BankAccountCard } from "../account/bankAccountCard";
import CustomTableContainer from "../../theme/components/tableContainer";
import { VendorSelect } from "../account/vendorDropDown";
import { LoadingTable, oppositeType, renderAmount } from "../../utils";
import { Trash2, Plus, ChevronDown, ChevronUp, Filter } from "react-feather";
import { Context } from "../../ContextWrapper";
import { w3cwebsocket as W3CWebSocket } from "websocket";
import { DropDown } from "../generic/dropDown";
import { TransactionList } from "../account/transactionList";
import { fetchCustomerAccounts } from "../../api/accounts";
import { VendorsQuickCreate } from "../vendors/vendorQuickCreate";
import { AddIcon } from "@chakra-ui/icons";
import { ThOrder } from "../generic/thOrder";

const QUICKBOOKS_PAGE_SIZE = 200;

const QUICKBOOKS_DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  memo: "",
  vendor: "",
  type: "",
};

// Fixed widths for amount columns (not resizable)
const AMOUNT_COLUMN_WIDTH = 90;

// Column widths for QuickBooks table (percentages)
const QB_COLUMN_WIDTHS = {
  lines: "5%",
  date: "8%",
  memo: "22%",
  vendor: "20%",
  type: "10%",
  docNumber: "5%",
  txnId: "5%",
  debit: "7%",
  credit: "7%",
};

// Shortened display names for QB transaction types
const QB_TYPE_DISPLAY = {
  "Bill": "Bill",
  "Bill Payment": "Bill Payment",
  "Bill Payment (Check)": "Bill Payment",
  "Bill Payment (Credit Card)": "Bill Payment",
  "Billable Charge": "Billable",
  "Cash Purchase": "Cash Purchase",
  "Charge": "Charge",
  "Check": "Check",
  "Credit": "Credit",
  "Credit Card Charge": "CC Charge",
  "Credit Card Credit": "CC Credit",
  "Credit Card Payment": "CC Payment",
  "Credit Memo": "Credit Memo",
  "Credit Refund": "Refund",
  "Deposit": "Deposit",
  "Estimate": "Estimate",
  "Expense": "Expense",
  "Inventory Qty Adjust": "Inv Adjust",
  "Invoice": "Invoice",
  "Journal Entry": "Journal Entry",
  "Payment": "Payment",
  "Purchase": "Purchase",
  "Purchase Order": "Purchase Order",
  "Receive Payment": "Receive Payment",
  "Refund": "Refund",
  "Sales Receipt": "Sales Receipt",
  "Statement": "Statement",
  "Time Activity": "Time Activity",
  "Transfer": "Transfer",
  "Vendor Credit": "Vendor Credit",
};

const getTypeDisplay = (type) => QB_TYPE_DISPLAY[type] || type || "";

export const ClientBankTransactions = ({ customer_id }) => {
  const [plaidItemList, setPlaidItemList] = useState([]);
  const [bankAccountList, setBankAccountList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState();
  const [selectedBankAccount, setSelectedBankAccount] = useState();
  const [accountList, setAccountList] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isTransferOpen,
    onOpen: onTransferOpen,
    onClose: onTransferClose,
  } = useDisclosure();
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursors, setPreviousCursors] = useState([]);
  const [balances, setBalances] = useState([]);
  const [ordering, setOrdering] = useState("date");
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const {
    isOpen: isVendorCreateOpen,
    onOpen: onVendorCreateOpen,
    onClose: onVendorCreateClose,
  } = useDisclosure();

  const [viewMode, setViewMode] = useState("plaid");
  const [quickbooksLoading, setQuickbooksLoading] = useState(false);
  const [quickbooksError, setQuickbooksError] = useState(null);
  const [quickbooksRows, setQuickbooksRows] = useState([]);
  const [quickbooksNext, setQuickbooksNext] = useState(null);
  const [quickbooksPrev, setQuickbooksPrev] = useState(null);
  const [expandedTxnIds, setExpandedTxnIds] = useState(new Set());
  const [quickbooksSort, setQuickbooksSort] = useState("-date");
  const [quickbooksFilters, setQuickbooksFilters] = useState(
    QUICKBOOKS_DEFAULT_FILTERS
  );

  useEffect(() => {
    refreshPlaidItemList(customer_id);
    refreshAccountList(customer_id);
    setLoading(false);
  }, [customer_id]);

  useEffect(() => {
    if (plaidItemList && plaidItemList.length > 0) {
      setSelectedItem(plaidItemList[0]);

      setBalances(
        plaidItemList
          .map((item) =>
            item.bankaccount_set.map((bankAccount) => ({
              id: bankAccount.accounting_account?.id,
              balance: bankAccount.accounting_account?.current_balance,
            }))
          )
          .flat()
      );
    }
  }, [plaidItemList]);

  useEffect(() => {
    if (selectedItem) {
      setBankAccountList(
        selectedItem.bankaccount_set.filter(
          (a) => a.accounting_account !== null
        )
      );
    }
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedBankAccount && bankAccountList.length > 0) {
      setSelectedBankAccount(bankAccountList[0]);
    }
  }, [bankAccountList, selectedBankAccount]);

  const refreshPlaidItemList = (id) => {
    if (id) {
      setLoading(true);
      axios
        .get(`/api/v2/customers/${id}/plaiditems`, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        })
        .then((res) => {
          setPlaidItemList(res.data);
        })
        .catch((err) => console.log(err));
    }
  };

  const refreshAccountList = (customer_id) => {
    if (customer_id) {
      const getData = async () => {
        const accountResults = await fetchCustomerAccounts(customer_id);
        setAccountList(
          accountResults.map((account) => ({
            value: account.id,
            label: account.name,
          }))
        );
      };
      getData();
    }
  };

  const refreshTransactionList = useCallback(
    (bankaccount_id, cursor = null) => {
      if (bankaccount_id) {
        setLoading(true);

        let cursorParam = null;
        if (cursor) {
          const urlObj = new URL(cursor);
          cursorParam = urlObj.searchParams.get("cursor");
        }

        const params = {
          cursor: cursorParam,
          ordering: ordering,
          exclude_added: true,
          ...(includeExcluded ? {} : { is_excluded: false }),
        };

        axios
          .get(`/api/v2/bankaccounts/${bankaccount_id}/transactions/`, {
            headers: { "Content-Type": "application/json" },
            params,
            withCredentials: true,
          })
          .then((res) => {
            setTransactions(res.data.results);
            setNextCursor(res.data.next);
            setPreviousCursors(res.data.previous);
            setLoading(false);
          })
          .catch((err) => console.log(err));
      }
    },
    [ordering, includeExcluded]
  );

  const fetchQuickbooksTransactions = useCallback(
    (url = null, sort = quickbooksSort, filters = quickbooksFilters) => {
      if (!customer_id && !url) {
        return;
      }

      setQuickbooksLoading(true);
      setQuickbooksError(null);

      // Build params with filters
      const params = { 
        page_size: QUICKBOOKS_PAGE_SIZE, 
        ordering: sort,
        group_by_txn: "true",
      };

      // Add filter params
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.memo) params.memo = filters.memo;
      if (filters.vendor) params.vendor = filters.vendor;
      if (filters.type) params.type = filters.type;

      let request;
      if (url) {
        // Extract path and query from absolute URL (backend returns absolute URLs)
        const urlObj = new URL(url);
        const relativeUrl = urlObj.pathname + urlObj.search;
        request = axios.get(relativeUrl, { withCredentials: true });
      } else {
        request = axios.get(
          `/api/v2/customers/${customer_id}/quickbooks/transactions/`,
          {
            params,
            withCredentials: true,
          }
        );
      }

      request
        .then((res) => {
          const rows = res.data.results || [];
          // Backend returns pre-grouped data, use directly
          setQuickbooksRows(rows);
          setQuickbooksNext(res.data.next);
          setQuickbooksPrev(res.data.previous);
          setExpandedTxnIds(new Set());
        })
        .catch((err) => {
          setQuickbooksError(
            err?.response?.data?.error || "Failed to load QuickBooks data"
          );
        })
        .finally(() => setQuickbooksLoading(false));
    },
    [customer_id, quickbooksSort, quickbooksFilters]
  );

  useEffect(() => {
    if (selectedBankAccount && viewMode === "plaid") {
      refreshTransactionList(selectedBankAccount.id);
    }
  }, [selectedBankAccount, refreshTransactionList, viewMode]);

  useEffect(() => {
    if (viewMode === "quickbooks" && customer_id) {
      fetchQuickbooksTransactions();
    }
  }, [viewMode, customer_id, fetchQuickbooksTransactions]);

  // Refetch when sort changes (server-side ordering)
  useEffect(() => {
    if (viewMode === "quickbooks" && customer_id && quickbooksRows.length > 0) {
      fetchQuickbooksTransactions(null, quickbooksSort, quickbooksFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickbooksSort]);

  // Refetch when filters change (server-side filtering)
  useEffect(() => {
    if (viewMode === "quickbooks" && customer_id) {
      fetchQuickbooksTransactions(null, quickbooksSort, quickbooksFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickbooksFilters]);

  // Server-side filtering and sorting - just use rows directly
  const sortedQuickbooksRows = quickbooksRows;

  const replaceTransaction = (t, action = "replace") => {
    if (action === "remove") {
      let removes = [t];
      if (t.constructor === Array) {
        removes = t;
      }
      removes.forEach((transaction) => {
        setTransactions((prev) => prev.filter((o) => o.id !== transaction.id));
        const current = selectedBankAccount;
        current.transaction_review_count -= 1;
        setBankAccountList((prev) =>
          prev.map((a) => (a.id === current.id ? current : a))
        );
      });
    } else {
      if (t.constructor === Array) {
        t.forEach((transaction) =>
          setTransactions((prev) =>
            prev.map((o) => (o.id === transaction.id ? transaction : o))
          )
        );
      } else {
        setTransactions(transactions.map((o) => (o.id === t.id ? t : o)));
      }
    }
  };

  const selectBankAccount = (item, bankAccount) => {
    setLoading(true);
    setOrdering("date");
    setSelectedItem(item);
    setSelectedBankAccount(bankAccount);
    setPreviousCursors([]);
  };

  const splitTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    onOpen();
  };
  const linkTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    onTransferOpen();
  };

  const handleNextPage = () => {
    if (nextCursor) {
      refreshTransactionList(selectedBankAccount.id, nextCursor);
    }
  };

  const handlePreviousPage = () => {
    const previousCursor = previousCursors;
    if (previousCursor) {
      refreshTransactionList(selectedBankAccount.id, previousCursor);
    }
  };

  useEffect(() => {
    if (!customer_id) {
      return;
    }

    const updateAccount = (accountId, balance) => {
      setBalances((prevBalances) =>
        prevBalances.map((account) =>
          account.id === accountId ? { ...account, balance } : account
        )
      );
    };

    let host = window.location.host;
    if (host.indexOf(':3000') === -1) {
      host = `wss://${host}`;
    } else {
      host = `ws://${host.replace(':3000', ':8000')}`;
    }

    const wsClient = new W3CWebSocket(
      `${host}/ws/${customer_id}/accountbalance/`
    );

    wsClient.onopen = () => {
      console.log('WebSocket Client Connected');
    };

    wsClient.onmessage = (message) => {
      try {
        const dataFromServer = JSON.parse(message.data);
        const payload = dataFromServer?.text;
        if (payload?.account_id) {
          updateAccount(payload.account_id, payload.balance);
        }
      } catch (error) {
        console.error('Unable to parse balance event', error);
      }
    };

    return () => {
      wsClient.close();
    };
  }, [customer_id]);

  const handleQuickbooksNext = useCallback(() => {
    if (quickbooksNext) {
      fetchQuickbooksTransactions(quickbooksNext);
    }
  }, [quickbooksNext, fetchQuickbooksTransactions]);

  const handleQuickbooksPrev = useCallback(() => {
    if (quickbooksPrev) {
      fetchQuickbooksTransactions(quickbooksPrev);
    }
  }, [quickbooksPrev, fetchQuickbooksTransactions]);

  const toggleExpandedGroup = (txnId) => {
    setExpandedTxnIds((prev) => {
      const copy = new Set(prev);
      if (copy.has(txnId)) {
        copy.delete(txnId);
      } else {
        copy.add(txnId);
      }
      return copy;
    });
  };

  const updateOrdering = (column) => {
    if (ordering.indexOf(column) !== -1) {
      if (ordering.indexOf("-") !== -1) {
        setOrdering(column);
      } else {
        setOrdering(`-${column}`);
      }
    } else {
      setOrdering(column);
    }
  };

  const viewToggle = (
    <HStack spacing={3}>
      <ButtonGroupToggle viewMode={viewMode} setViewMode={setViewMode} />
    </HStack>
  );

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)" display="flex" flexDirection="column">
      {selectedTransaction && isOpen && (
        <JournalSplit
          transaction={selectedTransaction}
          accountList={accountList}
          isOpen={isOpen}
          onClose={onClose}
          bankAccount={selectedBankAccount}
          replaceTransaction={replaceTransaction}
          customer_id={customer_id}
        />
      )}
      {selectedTransaction && isTransferOpen && (
        <TransferLink
          transaction={selectedTransaction}
          isOpen={isTransferOpen}
          onClose={onTransferClose}
          customer_id={customer_id}
          bankAccount={selectedBankAccount}
          plaidItemList={plaidItemList}
          replaceTransaction={replaceTransaction}
        />
      )}

      <Flex justify="space-between" align="center" mt={2} mb={4} w="full" flexShrink={0}>
        <Text fontSize="lg" fontWeight="600">
          Transactions
        </Text>
        {viewToggle}
      </Flex>

      <Box flex="1" minH="0" display="flex" flexDirection="column">
        {viewMode === "plaid" ? (
          <PlaidTransactionsView
          plaidItemList={plaidItemList}
          bankAccountList={bankAccountList}
          selectedBankAccount={selectedBankAccount}
          selectBankAccount={selectBankAccount}
          loading={loading}
          ordering={ordering}
          updateOrdering={updateOrdering}
          transactions={transactions}
          accountList={accountList}
          replaceTransaction={replaceTransaction}
          splitTransaction={splitTransaction}
          linkTransaction={linkTransaction}
          customer_id={customer_id}
          includeExcluded={includeExcluded}
          setIncludeExcluded={setIncludeExcluded}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          nextCursor={nextCursor}
          previousCursors={previousCursors}
          getAccountingBalance={(account_id) =>
            balances.find((account) => account.id === account_id)?.balance
          }
          isVendorCreateOpen={isVendorCreateOpen}
          onVendorCreateClose={onVendorCreateClose}
          onVendorCreateOpen={onVendorCreateOpen}
        />
      ) : (
        <QuickBooksTransactionsView
          rows={sortedQuickbooksRows}
          loading={quickbooksLoading}
          error={quickbooksError}
          onNext={handleQuickbooksNext}
          onPrev={handleQuickbooksPrev}
          hasNext={Boolean(quickbooksNext)}
          hasPrev={Boolean(quickbooksPrev)}
          expandedTxnIds={expandedTxnIds}
          toggleExpandedGroup={toggleExpandedGroup}
          quickbooksSort={quickbooksSort}
          setQuickbooksSort={setQuickbooksSort}
          quickbooksFilters={quickbooksFilters}
          setQuickbooksFilters={setQuickbooksFilters}
        />
      )}
      </Box>
    </Container>
  );
};

const ButtonGroupToggle = ({ viewMode, setViewMode }) => (
  <HStack spacing={0} borderRadius="full" bg="gray.100" p="2px">
    {[
      { label: "Plaid", value: "plaid" },
      { label: "QuickBooks", value: "quickbooks" },
    ].map((option) => (
      <Button
        key={option.value}
        size="sm"
        onClick={() => setViewMode(option.value)}
        borderRadius="full"
        variant={viewMode === option.value ? "solid" : "ghost"}
        colorScheme="blue"
        px={6}
      >
        {option.label}
      </Button>
    ))}
  </HStack>
);

const PlaidTransactionsView = ({
  plaidItemList,
  bankAccountList,
  selectedBankAccount,
  selectBankAccount,
  loading,
  ordering,
  updateOrdering,
  transactions,
  accountList,
  replaceTransaction,
  splitTransaction,
  linkTransaction,
  customer_id,
  includeExcluded,
  setIncludeExcluded,
  handlePreviousPage,
  handleNextPage,
  nextCursor,
  previousCursors,
  getAccountingBalance,
  isVendorCreateOpen,
  onVendorCreateClose,
  onVendorCreateOpen,
}) => (
  <>
    <Flex overflowX="auto" w="full" minW={0} flexShrink={0}>
      {plaidItemList.map((item) =>
        item.bankaccount_set.map((bankAccount) => (
          <BankAccountCard
            key={bankAccount.id}
            onClick={() => selectBankAccount(item, bankAccount)}
            bankName={item.name}
            bankAccount={bankAccount}
            balance={getAccountingBalance(
              bankAccount.accounting_account?.id
            )}
            variant={
              selectedBankAccount && bankAccount.id === selectedBankAccount.id
                ? "selected"
                : null
            }
          />
        ))
      )}
    </Flex>
    {plaidItemList.length === 0 && (
      <Box mt={6}>Link bank accounts on the Plaid menu.</Box>
    )}
    {plaidItemList.length > 0 && bankAccountList.length === 0 && (
      <Box mt={6}>Link the bank accounts to accounts on the Plaid menu.</Box>
    )}
    {bankAccountList.length > 0 && (
      <CustomTableContainer mt={5}>
        <Box maxHeight="calc(100vh - 480px)" m="4" bg="white" overflowX="auto">
          {loading && <LoadingTable />}
          {!loading && (
            <Table variant="unstyled" width="100%" size="sm">
              <Thead position="sticky" top={-1} zIndex={1}>
                <Tr>
                  <Td w="40px"></Td>
                  <ThOrder
                    header="date"
                    field="date"
                    ordering={ordering}
                    updateOrdering={updateOrdering}
                  />
                  <ThOrder
                    header="description"
                    field="name"
                    ordering={ordering}
                    updateOrdering={updateOrdering}
                    w="200px"
                  />
                  <Th w="250px">Vendor</Th>
                  <Th w="350px">Account</Th>
                  <Th textAlign="right">Spent</Th>
                  <Th textAlign="right">Received</Th>
                  <Th w="110px">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                <TransactionList
                  transactions={transactions}
                  bankAccount={selectedBankAccount}
                  accountList={accountList}
                  replaceTransaction={replaceTransaction}
                  splitTransaction={splitTransaction}
                  linkTransaction={linkTransaction}
                  customer_id={customer_id}
                  includeExcluded={includeExcluded}
                />
              </Tbody>
            </Table>
          )}
        </Box>
        <Stack direction="row" spacing={4} mt={4} justifyContent="center">
          {previousCursors && (
            <Button onClick={handlePreviousPage}>Previous</Button>
          )}
          {nextCursor && (
            <Button onClick={handleNextPage} disabled={!nextCursor}>
              Next
            </Button>
          )}
          <Spacer />
          <Checkbox
            name="includeExcluded"
            isChecked={includeExcluded}
            onChange={() => setIncludeExcluded(!includeExcluded)}
            fontWeight="normal"
          >
            Show excluded
          </Checkbox>
          <VendorsQuickCreate
            addVendorToList={() => {}}
            isOpen={isVendorCreateOpen}
            onClose={onVendorCreateClose}
            initialName={""}
          />
          <Button
            leftIcon={<AddIcon />}
            onClick={onVendorCreateOpen}
            aria-label="Create Vendor"
            mr={4}
          >
            Vendor
          </Button>
        </Stack>
      </CustomTableContainer>
    )}
  </>
);

const getNetAmounts = (value) => {
  const amount = Number(value) || 0;
  const absolute = Math.abs(amount);
  return {
    spent: amount > 0 ? absolute : 0,
    received: amount <= 0 ? absolute : 0,
  };
};

const QuickBooksTransactionsView = ({
  rows,
  loading,
  error,
  onNext,
  onPrev,
  hasNext,
  hasPrev,
  expandedTxnIds,
  toggleExpandedGroup,
  quickbooksSort,
  setQuickbooksSort,
  quickbooksFilters,
  setQuickbooksFilters,
}) => {
  const handleSort = (field) => {
    setQuickbooksSort((prev) => {
      const normalized = prev.startsWith('-') ? prev.slice(1) : prev;
      if (normalized !== field) {
        return field;
      }
      return prev.startsWith('-') ? field : `-${field}`;
    });
  };

  const normalizedSort = quickbooksSort.startsWith('-')
    ? quickbooksSort.slice(1)
    : quickbooksSort;
  const isDesc = quickbooksSort.startsWith('-');

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    quickbooksFilters.dateFrom ||
    quickbooksFilters.dateTo ||
    quickbooksFilters.memo ||
    quickbooksFilters.vendor ||
    quickbooksFilters.type
  );

  return (
    <CustomTableContainer mt={5} display="flex" flexDirection="column" flex="1" minH="0">
      <Box flex="1" m="4" bg="white" overflowX="auto" overflowY="auto">
        {error && (
          <Box mb={4} color="red.500">
            {error}
          </Box>
        )}
        {hasActiveFilters && (
          <HStack spacing={2} mb={3} p={2} bg="blue.50" borderRadius="md" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="medium" color="blue.700">Active Filters:</Text>
            {quickbooksFilters.dateFrom && (
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>From: {quickbooksFilters.dateFrom}</TagLabel>
                <TagCloseButton onClick={() => setQuickbooksFilters((prev) => ({ ...prev, dateFrom: "" }))} />
              </Tag>
            )}
            {quickbooksFilters.dateTo && (
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>To: {quickbooksFilters.dateTo}</TagLabel>
                <TagCloseButton onClick={() => setQuickbooksFilters((prev) => ({ ...prev, dateTo: "" }))} />
              </Tag>
            )}
            {quickbooksFilters.memo && (
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>Desc: {quickbooksFilters.memo}</TagLabel>
                <TagCloseButton onClick={() => setQuickbooksFilters((prev) => ({ ...prev, memo: "" }))} />
              </Tag>
            )}
            {quickbooksFilters.vendor && (
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>Vendor: {quickbooksFilters.vendor}</TagLabel>
                <TagCloseButton onClick={() => setQuickbooksFilters((prev) => ({ ...prev, vendor: "" }))} />
              </Tag>
            )}
            {quickbooksFilters.type && (
              <Tag size="sm" colorScheme="blue" borderRadius="full">
                <TagLabel>Type: {quickbooksFilters.type}</TagLabel>
                <TagCloseButton onClick={() => setQuickbooksFilters((prev) => ({ ...prev, type: "" }))} />
              </Tag>
            )}
          </HStack>
        )}
        {loading && <LoadingTable />}
        {!loading && (
          <Table variant="unstyled" width="100%" size="sm" sx={{ tableLayout: "fixed" }}>
            <Thead position="sticky" top={-1} zIndex={1}>
              <Tr>
                <Th w={QB_COLUMN_WIDTHS.lines} cursor="pointer" onClick={() => handleSort("line_count")}>
                  <HStack spacing={1}>
                    <Text m="0">Lines</Text>
                    {normalizedSort === "line_count" ? (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />) : <ChevronDown size={14} color="gray" />}
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.date}>
                  <HStack spacing={1}>
                    <HStack spacing={1} cursor="pointer" onClick={() => handleSort("date")}>
                      <Text m="0">Date</Text>
                      {normalizedSort === "date" && (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
                    </HStack>
                    <ColumnFilterPopover
                      label="Date Filter"
                      isActive={Boolean(quickbooksFilters.dateFrom || quickbooksFilters.dateTo)}
                      onClear={() => setQuickbooksFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }))}
                    >
                      {({ onClose }) => (
                        <VStack spacing={3} align="stretch">
                          <Box>
                            <Text fontSize="xs" mb={1}>From</Text>
                            <Input
                              size="sm"
                              type="date"
                              value={quickbooksFilters.dateFrom}
                              onChange={(e) => {
                                setQuickbooksFilters((prev) => ({ ...prev, dateFrom: e.target.value }));
                                if (e.target.value) onClose();
                              }}
                            />
                          </Box>
                          <Box>
                            <Text fontSize="xs" mb={1}>To</Text>
                            <Input
                              size="sm"
                              type="date"
                              value={quickbooksFilters.dateTo}
                              onChange={(e) => {
                                setQuickbooksFilters((prev) => ({ ...prev, dateTo: e.target.value }));
                                if (e.target.value) onClose();
                              }}
                            />
                          </Box>
                        </VStack>
                      )}
                    </ColumnFilterPopover>
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.memo}>
                  <HStack spacing={1}>
                    <HStack spacing={1} cursor="pointer" onClick={() => handleSort("memo")}>
                      <Text m="0">Memo</Text>
                      {normalizedSort === "memo" && (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
                    </HStack>
                    <ColumnFilterPopover
                      label="Description Filter"
                      isActive={Boolean(quickbooksFilters.memo)}
                      onClear={() => setQuickbooksFilters((prev) => ({ ...prev, memo: "" }))}
                    >
                      {({ onClose }) => (
                        <Input
                          size="sm"
                          placeholder="Contains..."
                          value={quickbooksFilters.memo}
                          onChange={(e) => setQuickbooksFilters((prev) => ({ ...prev, memo: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") onClose(); }}
                        />
                      )}
                    </ColumnFilterPopover>
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.vendor}>
                  <HStack spacing={1}>
                    <Text m="0">Vendor</Text>
                    <ColumnFilterPopover
                      label="Vendor Filter"
                      isActive={Boolean(quickbooksFilters.vendor)}
                      onClear={() => setQuickbooksFilters((prev) => ({ ...prev, vendor: "" }))}
                    >
                      {({ onClose }) => (
                        <Input
                          size="sm"
                          placeholder="Contains..."
                          value={quickbooksFilters.vendor}
                          onChange={(e) => setQuickbooksFilters((prev) => ({ ...prev, vendor: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") onClose(); }}
                        />
                      )}
                    </ColumnFilterPopover>
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.type}>
                  <HStack spacing={1}>
                    <HStack spacing={1} cursor="pointer" onClick={() => handleSort("type")}>
                      <Text m="0">Type</Text>
                      {normalizedSort === "type" && (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
                    </HStack>
                    <ColumnFilterPopover
                      label="Type Filter"
                      isActive={Boolean(quickbooksFilters.type)}
                      onClear={() => setQuickbooksFilters((prev) => ({ ...prev, type: "" }))}
                    >
                      {({ onClose }) => (
                        <Select
                          size="sm"
                          placeholder="All Types"
                          value={quickbooksFilters.type}
                          onChange={(e) => {
                            setQuickbooksFilters((prev) => ({ ...prev, type: e.target.value }));
                            onClose();
                          }}
                        >
                          <option value="Bill">Bill</option>
                          <option value="Bill Payment">Bill Payment</option>
                          <option value="Bill Payment (Check)">Bill Payment</option>
                          <option value="Bill Payment (Credit Card)">Bill Payment</option>
                          <option value="Billable Charge">Billable</option>
                          <option value="Cash Purchase">Cash Purchase</option>
                          <option value="Charge">Charge</option>
                          <option value="Check">Check</option>
                          <option value="Credit">Credit</option>
                          <option value="Credit Card Charge">CC Charge</option>
                          <option value="Credit Card Credit">CC Credit</option>
                          <option value="Credit Card Payment">CC Payment</option>
                          <option value="Credit Memo">Credit Memo</option>
                          <option value="Credit Refund">Refund</option>
                          <option value="Deposit">Deposit</option>
                          <option value="Estimate">Estimate</option>
                          <option value="Expense">Expense</option>
                          <option value="Inventory Qty Adjust">Inv Adjust</option>
                          <option value="Invoice">Invoice</option>
                          <option value="Journal Entry">Journal Entry</option>
                          <option value="Payment">Payment</option>
                          <option value="Purchase">Purchase</option>
                          <option value="Purchase Order">Purchase Order</option>
                          <option value="Receive Payment">Receive Payment</option>
                          <option value="Refund">Refund</option>
                          <option value="Sales Receipt">Sales Receipt</option>
                          <option value="Statement">Statement</option>
                          <option value="Time Activity">Time Activity</option>
                          <option value="Transfer">Transfer</option>
                          <option value="Vendor Credit">Vendor Credit</option>
                        </Select>
                      )}
                    </ColumnFilterPopover>
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.docNumber}>Doc #</Th>
                <Th w={QB_COLUMN_WIDTHS.txnId}>TX ID</Th>
                <Th w={QB_COLUMN_WIDTHS.debit} textAlign="right">
                  <HStack spacing={1} justify="flex-end">
                    <HStack spacing={1} cursor="pointer" onClick={() => handleSort("total")}>
                      <Text m="0">Debit</Text>
                      {normalizedSort === "total" && (isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}
                    </HStack>
                  </HStack>
                </Th>
                <Th w={QB_COLUMN_WIDTHS.credit} textAlign="right">Credit</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((group) => {
                const isExpanded = expandedTxnIds.has(group.txnId);
                const { spent, received } = getNetAmounts(group.total);
                const lineCount = group.lines?.length || 0;
                return (
                  <React.Fragment key={group.key}>
                    <Tr
                      bg="white"
                      _hover={{ bg: "gray.50", cursor: "pointer" }}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      onClick={() => toggleExpandedGroup(group.txnId)}
                      py={1}
                    >
                      <Td py={2} w={QB_COLUMN_WIDTHS.lines}>
                        <HStack spacing={1}>
                          <Icon
                            as={isExpanded ? ChevronUp : ChevronDown}
                            boxSize={4}
                            color="gray.500"
                          />
                          <Tag size="sm" colorScheme="blue" borderRadius="full" minW="24px" justifyContent="center">
                            <TagLabel fontSize="xs">{lineCount}</TagLabel>
                          </Tag>
                        </HStack>
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.date}>{group.date || ""}</Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.memo}>
                        <Text noOfLines={1}>{group.memo || ""}</Text>
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.vendor}>
                        <Text noOfLines={1}>{group.vendor || ""}</Text>
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.type}>
                        <Text noOfLines={1}>{getTypeDisplay(group.type)}</Text>
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.docNumber} fontSize="xs">
                        {group.docNumber || ""}
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.txnId} fontSize="xs">
                        <Text noOfLines={1}>{group.txnId || ""}</Text>
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.debit} textAlign="right">
                        {spent > 0 ? renderAmount(spent) : ""}
                      </Td>
                      <Td py={2} w={QB_COLUMN_WIDTHS.credit} textAlign="right">
                        {received > 0 ? renderAmount(received) : ""}
                      </Td>
                    </Tr>
                    {isExpanded && (
                      <Tr>
                        <Td colSpan={9} p={0} bg="gray.50">
                          <Collapse in={isExpanded} animateOpacity>
                            <Box py={2} px={4}>
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th w="80px" py={1} fontSize="xs">Date</Th>
                                    <Th py={1} fontSize="xs">Memo</Th>
                                    <Th py={1} fontSize="xs">Vendor</Th>
                                    <Th py={1} fontSize="xs">Account</Th>
                                    <Th py={1} fontSize="xs">Split</Th>
                                    <Th w={`${AMOUNT_COLUMN_WIDTH}px`} py={1} fontSize="xs" textAlign="right">Debit</Th>
                                    <Th w={`${AMOUNT_COLUMN_WIDTH}px`} py={1} fontSize="xs" textAlign="right">Credit</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {[...group.lines].sort((a, b) => {
                                    // Debits (positive) first, credits (negative/zero) second, preserve original order within each
                                    const aIsDebit = a.amount > 0;
                                    const bIsDebit = b.amount > 0;
                                    if (aIsDebit === bIsDebit) return 0; // preserve original order
                                    return aIsDebit ? -1 : 1; // debits before credits
                                  }).map((line) => {
                                    const lineAmounts = getNetAmounts(line.amount);
                                    return (
                                      <Tr key={line.id} _hover={{ bg: "gray.100" }}>
                                        <Td w="80px" py={1} fontSize="sm">{line.date || group.date || ""}</Td>
                                        <Td py={1} fontSize="sm"><Text noOfLines={1}>{line.memo || ""}</Text></Td>
                                        <Td py={1} fontSize="sm"><Text noOfLines={1}>{line.name || ""}</Text></Td>
                                        <Td py={1} fontSize="sm">{line.account || line.account_name || ""}</Td>
                                        <Td py={1} fontSize="sm">{line.split || line.split_acc || ""}</Td>
                                        <Td w={`${AMOUNT_COLUMN_WIDTH}px`} py={1} fontSize="sm" textAlign="right">
                                          {lineAmounts.spent > 0 ? renderAmount(lineAmounts.spent) : ""}
                                        </Td>
                                        <Td w={`${AMOUNT_COLUMN_WIDTH}px`} py={1} fontSize="sm" textAlign="right">
                                          {lineAmounts.received > 0 ? renderAmount(lineAmounts.received) : ""}
                                        </Td>
                                      </Tr>
                                    );
                                  })}
                                </Tbody>
                              </Table>
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        )}
        {!loading && rows.length === 0 && (
          <Center py={10}>
            <Text color="gray.500">No QuickBooks transactions found.</Text>
          </Center>
        )}
      </Box>
      <Stack direction="row" spacing={4} mt={4} justifyContent="center">
        {hasPrev && <Button onClick={onPrev}>Previous</Button>}
        {hasNext && (
          <Button onClick={onNext} disabled={!hasNext}>
            Next
          </Button>
        )}
      </Stack>
    </CustomTableContainer>
  );
};

const ColumnFilterPopover = ({ label, isActive, onClear, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleClear = () => {
    if (typeof onClear === "function") {
      onClear();
    }
    onClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      closeOnBlur={true}
      closeOnEsc={true}
      returnFocusOnClose={false}
      isLazy
    >
      <PopoverTrigger>
        <IconButton
          aria-label={label}
          icon={<Filter size={12} />}
          variant="ghost"
          size="xs"
          colorScheme={isActive ? "blue" : "gray"}
          ml={1}
        />
      </PopoverTrigger>
      <PopoverContent width="220px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="bold" fontSize="sm">
          {label}
        </PopoverHeader>
        <PopoverBody>
          {typeof children === "function" ? children({ onClose }) : children}
        </PopoverBody>
        <PopoverFooter display="flex" justifyContent="flex-end" gap={2}>
          <Button size="xs" variant="ghost" onClick={handleClear}>
            Clear
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

const TransferLink = ({
  transaction,
  isOpen,
  onClose,
  customer_id,
  bankAccount,
  plaidItemList,
  replaceTransaction,
}) => {
  const [accountList, setAccountList] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const accounts = plaidItemList
      .flatMap((item) => {
        for (var i in item.bankaccount_set) {
          item.bankaccount_set[i].item_name = item.name;
        }
        return item.bankaccount_set;
      })
      .filter((a) => a.accounting_account && a.id !== bankAccount.id)
      .map((a) => ({ value: a.id, label: a.item_name + ": " + a.name }));
    setAccountList(accounts);
  }, [plaidItemList, bankAccount]);

  useEffect(() => {
    if (selectedBankAccount) {
      let start = new Date(Date.parse(transaction.date));
      start.setDate(start.getDate() - 15);
      start = start.toISOString().split("T")[0];

      let end = new Date(Date.parse(transaction.date));
      end.setDate(end.getDate() + 15);
      end = end.toISOString().split("T")[0];

      axios
        .get(`/api/v2/bankaccounts/${selectedBankAccount}/transactions/`, {
          headers: { "Content-Type": "application/json" },
          params: {
            ordering: "date",
            date_after: start,
            date_before: end,
            amount: transaction.amount * -1,
            exclude_added: true,
          },
          withCredentials: true,
        })
        .then((res) => {
          setTransactions(res.data.results);
        })
        .catch((err) => console.log(err));
    }
  }, [selectedBankAccount, transaction]);

  const submitTransfer = () => {
    axios
      .post(`/api/v2/customers/${customer_id}/linktransfer/`, {
        bankaccount_id: bankAccount.id,
        transaction_id: transaction.id,
        link_bankaccount_id: selectedBankAccount,
        link_transaction_id: selectedTransaction?.id,
      })
      .then((res) => {
        replaceTransaction(transaction, "remove");
        onClose();
      })
      .catch((err) => console.log(err));
  };

  const updateTransferAccount = (account) => {
    setSelectedBankAccount(account.value);
  };

  const selectTransaction = (t, e) => {
    if (e.target.checked) {
      setSelectedTransaction(t);
    } else {
      setSelectedTransaction(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxH="800px" maxW="1000px">
        <ModalHeader>Split Transaction</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Center pb="25px">
            <DropDown
              name="account"
              options={accountList}
              onChange={updateTransferAccount}
              w="400px"
            />
          </Center>
          <Table variant="unstyled">
            <Tbody>
              {transactions &&
                transactions.map((t) => (
                  <Tr key={t.id}>
                    <Td>
                      <Checkbox
                        isChecked={t.id === selectedTransaction?.id}
                        onChange={(e) => selectTransaction(t, e)}
                      />
                    </Td>
                    <Td>{t.date}</Td>
                    <Td>{t.name}</Td>
                    <Td>{t.amount}</Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
          {selectedBankAccount && transactions.length === 0 && (
            <Center>
              <Text pt={"3"}>No transactions found.</Text>
            </Center>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button onClick={submitTransfer}>Link Transfer</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const JournalSplit = ({
  transaction,
  accountList,
  isOpen,
  onClose,
  bankAccount,
  replaceTransaction,
  customer_id,
}) => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [index, setIndex] = useState(1);
  const [total, setTotal] = useState(0.0);
  const context = React.useContext(Context);

  const account_type = bankAccount.accounting_account.account_type;
  const bank_account_type =
    transaction.amount <= 0 ? account_type : oppositeType(account_type);

  const generateBlankJournalEntry = useCallback(
    (index, amount, type) => {
      return {
        id: null,
        index: index,
        date: transaction.date,
        account: null,
        entry_type: type,
        description: "",
        amount: Math.abs(amount),
      };
    },
    [transaction]
  );

  useEffect(() => {
    setTotal(0.0);
    if (transaction.journal_entries.length > 0) {
      const entries = transaction.journal_entries.map((e, i) => {
        e.index = i;
        return e;
      });
      setIndex(entries.length + 1);
      setJournalEntries(entries);
    } else {
      const balanceEntry = {
        index: 999,
        account: bankAccount.accounting_account.id,
        entry_type: bank_account_type,
        date: transaction.date,
        amount: Math.abs(transaction.amount),
        description: transaction.description ?? transaction.name,
        added: true,
      };
      const newEntry = generateBlankJournalEntry(
        0,
        Math.abs(transaction.amount),
        oppositeType(bank_account_type)
      );
      setJournalEntries([newEntry, balanceEntry]);
    }
    setVendor(transaction.vendor);
  }, [transaction, generateBlankJournalEntry, bankAccount, bank_account_type]);

  useEffect(() => {
    setTotal(
      journalEntries
        .filter((e) => e.entry_type !== bank_account_type)
        .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0)
        .toFixed(2)
    );
  }, [journalEntries, bank_account_type]);

  const addLine = () => {
    const newEntry = generateBlankJournalEntry(
      index,
      0,
      oppositeType(bank_account_type)
    );
    setJournalEntries((prevList) => [...prevList, newEntry]);
    setIndex((prev) => prev + 1);
  };

  const removeLine = (index) => {
    setJournalEntries((prevList) =>
      prevList.filter((entry) => entry.index !== index)
    );
  };

  const changeEntryAmount = (entry, e) => {
    entry.amount = Number(e.target.value).toFixed(2);
    setJournalEntries((prevList) =>
      prevList.map((e) => (e.index === entry.index ? entry : e))
    );
  };

  const changeEntryDescription = (entry, e) => {
    entry.description = e.target.value;
    setJournalEntries((prevList) =>
      prevList.map((e) => (e.index === entry.index ? entry : e))
    );
  };

  const updateVendor = (vendor) => {
    setVendor(vendor.value);
  };

  const updateEntryAccount = (account, { entry }) => {
    entry.account = account.value;
    setJournalEntries((prevList) =>
      prevList.map((e) => (e.index === entry.index ? entry : e))
    );
  };

  const submitSplit = () => {
    if (!vendor) {
      context.fireToast("Error", "Please select vendor.", "error");
      return;
    }
    if (Number(total) !== Math.abs(transaction.amount)) {
      context.fireToast(
        "Error",
        "Split amount doesn't match original amount.",
        "error"
      );
      return;
    }
    if (journalEntries.length === 1) {
      context.fireToast(
        "Error",
        "Add another line to split transaction.",
        "error"
      );
      return;
    }
    for (let entry of journalEntries) {
      if (!entry.account) {
        context.fireToast(
          "Error",
          "Please select account on all lines.",
          "error"
        );
        break;
      }
      if (entry.amount <= 0) {
        context.fireToast("Error", "Invalid amount on line.", "error");
        break;
      }
    }

    var data = journalEntries.map((entry) => {
      var object = {
        customer: customer_id,
        account: entry.account,
        entry_type: entry.entry_type,
        date: entry.date,
        amount: entry.amount,
        description: entry.description,
        added: true,
      };

      if (entry.id) {
        object.id = entry.id;
      }

      return object;
    });

    axios
      .patch(
        `/api/v2/bankaccounts/${transaction.bankaccount}/transactions/${transaction.id}/`,
        {
          vendor: vendor,
          journal_entries: data,
        }
      )
      .then((res) => {
        replaceTransaction(res.data, "remove");
        closeSplitWindow();
        context.fireToast(
          "Success",
          "Transaction split successfully.",
          "success"
        );
      })
      .catch((err) => console.log(err));
  };

  const closeSplitWindow = () => {
    setJournalEntries([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={closeSplitWindow} isCentered>
      <ModalOverlay />
      <ModalContent maxH="800px" maxW="1000px">
        <ModalHeader>Split Transaction</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align={"start"}>
            <Text>
              Please input the fields below to this split transaction in it's
              relevant accounts.
            </Text>
            <Flex w="100%">
              <VStack spacing="0px" align={"start"}>
                <Text m="0">Total Amount</Text>
                <Text as="b" fontSize={"4xl"}>
                  {renderAmount(Math.abs(transaction.amount))}
                </Text>
              </VStack>
              <Spacer />
              <VStack spacing="2px" align={"start"}>
                <Text m="0">Vendor</Text>
                <VendorSelect
                  selectedVendorID={vendor}
                  onChange={updateVendor}
                />
              </VStack>
            </Flex>
            <Table variant="unstyled">
              <Thead>
                <Tr>
                  <Th>Account</Th>
                  <Th>Description</Th>
                  <Th>Amount</Th>
                  <Th style={{ width: "118px" }}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {journalEntries
                  .filter((e) => e.entry_type !== bank_account_type)
                  .map((entry) => (
                    <Tr key={entry.index || 0}>
                      <Td>
                        <DropDown
                          name="account"
                          options={accountList}
                          selectedValue={entry.account}
                          metaData={{ entry }}
                          onChange={updateEntryAccount}
                        />
                      </Td>
                      <Td>
                        <Input
                          type="text"
                          value={entry.description}
                          onChange={(e) => changeEntryDescription(entry, e)}
                        />
                      </Td>
                      <Td>
                        <NumberInput defaultValue={entry.amount}>
                          <NumberInputField
                            onChange={(e) => changeEntryAmount(entry, e)}
                          />
                        </NumberInput>
                      </Td>
                      <Td>
                        <IconButton
                          variant="outline"
                          colorScheme="black"
                          aria-label="Delete line"
                          icon={<Trash2 />}
                          onClick={() => removeLine(entry.index)}
                        />
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex w="100%">
              <VStack align={"start"}>
                <Button
                  rightIcon={<Plus />}
                  variant={"ghost"}
                  onClick={addLine}
                >
                  Add line
                </Button>
              </VStack>
              <Spacer />
              <VStack align={"start"}>
                <HStack>
                  <Text w="150px" align={"right"}>
                    Split Amount:
                  </Text>
                  <Text w="90px">{renderAmount(Math.abs(total))}</Text>
                </HStack>
                <HStack>
                  <Text w="150px" align={"right"}>
                    Original Amount:
                  </Text>
                  <Text w="90px">
                    {renderAmount(Math.abs(transaction.amount))}
                  </Text>
                </HStack>
                <HStack>
                  <Text w="150px" align={"right"}>
                    Difference:
                  </Text>
                  <Text
                    w="90px"
                    color={
                      Number(total) !== Math.abs(transaction.amount)
                        ? "red"
                        : "black"
                    }
                  >
                    {renderAmount(
                      Math.abs(total - Math.abs(transaction.amount))
                    )}
                  </Text>
                </HStack>
              </VStack>
            </Flex>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={closeSplitWindow}>
            Close
          </Button>
          <Button onClick={submitSplit}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

